import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDate as formatDateUtil } from '../utils/dateUtils';
import {
  Users,
  DollarSign,
  Calendar,
  Clock,
  Plus,
  Play,
  FileText,
  TrendingUp,
  Calculator,
  Building,
  X,
  CreditCard,
  ExternalLink,
  Copy,
  CheckCircle,
  Banknote
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Payroll() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [showRunForm, setShowRunForm] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);

  const [employeeForm, setEmployeeForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    hire_date: new Date().toISOString().split('T')[0],
    department: '',
    job_title: '',
    pay_type: 'SALARY',
    pay_rate: '',
    pay_frequency: 'BIWEEKLY',
    ssn: '',
    filing_status: 'SINGLE',
    w4_step2_checkbox: false,
    w4_dependents_amount: 0,
    w4_extra_withholding: 0,
    bank_name: '',
    bank_routing: '',
    bank_account: '',
    bank_account_type: 'CHECKING'
  });

  const [showPaymentExport, setShowPaymentExport] = useState(false);
  const [paymentExportData, setPaymentExportData] = useState(null);
  const [paymentChecklist, setPaymentChecklist] = useState({
    employeePaymentsInitiated: false,
    eftpsSubmitted: false,
    flSutaSubmitted: false
  });
  const [copiedEmployeeList, setCopiedEmployeeList] = useState(false);

  const [runForm, setRunForm] = useState({
    pay_period_start: '',
    pay_period_end: '',
    pay_date: ''
  });

  // Employee selection for calculating payroll
  const [showEmployeeSelect, setShowEmployeeSelect] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [pendingCalculateRunId, setPendingCalculateRunId] = useState(null);

  // Mercury integration state
  const [mercuryStatus, setMercuryStatus] = useState(null);
  const [mercuryRecipients, setMercuryRecipients] = useState([]);
  const [employeeMercuryMappings, setEmployeeMercuryMappings] = useState({});
  const [mercurySubmitting, setMercurySubmitting] = useState(false);
  const [mercuryResult, setMercuryResult] = useState(null);
  const [mercurySyncing, setMercurySyncing] = useState(false);
  const [mercurySyncResult, setMercurySyncResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [dashboardRes, employeesRes, runsRes] = await Promise.all([
        axios.get(`${API_URL}/api/payroll/dashboard`, { headers }),
        axios.get(`${API_URL}/api/payroll/employees`, { headers }),
        axios.get(`${API_URL}/api/payroll/runs`, { headers })
      ]);

      setDashboard(dashboardRes.data);
      setEmployees(employeesRes.data);
      setPayrollRuns(runsRes.data);
    } catch (err) {
      console.error('Error fetching payroll data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return formatDateUtil(dateString);
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/payroll/employees`, employeeForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEmployeeForm(false);
      setEmployeeForm({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        hire_date: new Date().toISOString().split('T')[0],
        department: '',
        job_title: '',
        pay_type: 'SALARY',
        pay_rate: '',
        pay_frequency: 'BIWEEKLY',
        ssn: '',
        filing_status: 'SINGLE',
        w4_step2_checkbox: false,
        w4_dependents_amount: 0,
        w4_extra_withholding: 0,
        bank_name: '',
        bank_routing: '',
        bank_account: '',
        bank_account_type: 'CHECKING'
      });
      fetchData();
    } catch (err) {
      console.error('Error creating employee:', err);
      alert('Failed to create employee: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCreatePayrollRun = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/payroll/runs`, runForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowRunForm(false);
      setRunForm({ pay_period_start: '', pay_period_end: '', pay_date: '' });
      fetchData();
    } catch (err) {
      console.error('Error creating payroll run:', err);
      alert('Failed to create payroll run');
    }
  };

  const openEmployeeSelectModal = (runId) => {
    // Pre-select all active employees
    setSelectedEmployeeIds(employees.map(emp => emp.id));
    setPendingCalculateRunId(runId);
    setShowEmployeeSelect(true);
  };

  const handleCalculatePayroll = async () => {
    if (!pendingCalculateRunId) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/payroll/runs/${pendingCalculateRunId}/calculate`,
        { employee_ids: selectedEmployeeIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowEmployeeSelect(false);
      setPendingCalculateRunId(null);
      fetchData();
      alert('Payroll calculated successfully!');
    } catch (err) {
      console.error('Error calculating payroll:', err);
      alert('Failed to calculate: ' + (err.response?.data?.detail || err.message));
    }
  };

  const toggleEmployeeSelection = (empId) => {
    setSelectedEmployeeIds(prev =>
      prev.includes(empId)
        ? prev.filter(id => id !== empId)
        : [...prev, empId]
    );
  };

  const selectAllEmployees = () => {
    setSelectedEmployeeIds(employees.map(emp => emp.id));
  };

  const selectNoneEmployees = () => {
    setSelectedEmployeeIds([]);
  };

  const handleApprovePayroll = async (runId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/payroll/runs/${runId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Error approving payroll:', err);
      alert('Failed to approve');
    }
  };

  const handleMarkPaid = async (runId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/payroll/runs/${runId}/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Error marking paid:', err);
      alert('Failed to mark as paid');
    }
  };

  const handleShowPaymentExport = async (runId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch payment export data
      const response = await axios.get(`${API_URL}/api/payroll/runs/${runId}/payment-export`, { headers });
      setPaymentExportData({ ...response.data, run_id: runId });
      setShowPaymentExport(true);

      // Reset states
      setPaymentChecklist({
        employeePaymentsInitiated: false,
        eftpsSubmitted: false,
        flSutaSubmitted: false
      });
      setCopiedEmployeeList(false);
      setMercuryResult(null);
      setEmployeeMercuryMappings({});

      // Fetch Mercury status and recipients
      try {
        const statusRes = await axios.get(`${API_URL}/api/payroll/mercury/status`, { headers });
        setMercuryStatus(statusRes.data);

        if (statusRes.data.configured && statusRes.data.connected) {
          const recipientsRes = await axios.get(`${API_URL}/api/payroll/mercury/recipients`, { headers });
          setMercuryRecipients(recipientsRes.data.recipients || []);
        }
      } catch (mercuryErr) {
        console.log('Mercury not configured:', mercuryErr);
        setMercuryStatus({ configured: false, connected: false });
      }
    } catch (err) {
      console.error('Error loading payment export:', err);
      alert('Failed to load payment data: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleMercuryMappingChange = (employeeId, recipientId) => {
    setEmployeeMercuryMappings(prev => ({
      ...prev,
      [employeeId]: recipientId
    }));
  };

  const handleSubmitToMercury = async () => {
    if (!paymentExportData?.run_id) return;

    const mappings = Object.entries(employeeMercuryMappings)
      .filter(([_, recipientId]) => recipientId)
      .map(([employeeId, recipientId]) => ({
        employee_id: employeeId,
        mercury_recipient_id: recipientId
      }));

    if (mappings.length === 0) {
      alert('Please map at least one employee to a Mercury recipient');
      return;
    }

    setMercurySubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/payroll/mercury/submit-payments`, {
        run_id: paymentExportData.run_id,
        employee_mappings: mappings
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMercuryResult(response.data);

      if (response.data.success) {
        setPaymentChecklist(prev => ({ ...prev, employeePaymentsInitiated: true }));
      }
    } catch (err) {
      console.error('Error submitting to Mercury:', err);
      setMercuryResult({
        success: false,
        error: err.response?.data?.detail || err.message
      });
    } finally {
      setMercurySubmitting(false);
    }
  };

  const handleSyncToMercury = async () => {
    setMercurySyncing(true);
    setMercurySyncResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/payroll/mercury/sync-employees`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMercurySyncResult(response.data);

      // Refresh recipients after sync
      if (response.data.synced?.length > 0) {
        const recipientsRes = await axios.get(`${API_URL}/api/payroll/mercury/recipients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMercuryRecipients(recipientsRes.data.recipients || []);

        // Auto-map synced employees to their new recipients
        const newMappings = { ...employeeMercuryMappings };
        response.data.synced.forEach(s => {
          newMappings[s.employee_id] = s.mercury_recipient_id;
        });
        setEmployeeMercuryMappings(newMappings);
      }
    } catch (err) {
      console.error('Error syncing to Mercury:', err);
      setMercurySyncResult({
        success: false,
        message: err.response?.data?.detail || err.message
      });
    } finally {
      setMercurySyncing(false);
    }
  };

  const copyEmployeeListToClipboard = () => {
    if (!paymentExportData?.employee_payments) return;

    const lines = paymentExportData.employee_payments.map(emp =>
      `${emp.employee_name}\t${emp.bank_name || 'N/A'}\t${emp.routing_number || 'N/A'}\t${emp.account_number || 'N/A'}\t${emp.account_type || 'N/A'}\t$${emp.net_pay.toFixed(2)}`
    );
    const header = 'Name\tBank\tRouting\tAccount\tType\tAmount';
    const text = [header, ...lines].join('\n');

    navigator.clipboard.writeText(text).then(() => {
      setCopiedEmployeeList(true);
      setTimeout(() => setCopiedEmployeeList(false), 3000);
    });
  };

  const viewPayrollRun = async (runId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/payroll/runs/${runId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedRun(response.data);
    } catch (err) {
      console.error('Error fetching run details:', err);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      DRAFT: 'bg-slate-500/20 text-slate-400',
      PENDING: 'bg-yellow-500/20 text-yellow-400',
      APPROVED: 'bg-blue-500/20 text-blue-400',
      PAID: 'bg-green-500/20 text-green-400',
      VOIDED: 'bg-red-500/20 text-red-400'
    };
    return colors[status] || 'bg-slate-500/20 text-slate-300';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900/30 to-slate-900 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white">Payroll</h1>
          <p className="text-cyan-300 mt-1">Manage employees, run payroll, and track taxes</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20 mb-6">
        <nav className="flex border-b border-cyan-500/20">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
            { id: 'employees', label: 'Employees', icon: Users },
            { id: 'payroll-runs', label: 'Payroll Runs', icon: Calendar },
            { id: 'tax-calculator', label: 'Tax Calculator', icon: Calculator }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition ${
                activeTab === tab.id
                  ? 'border-b-2 border-cyan-500 text-cyan-300'
                  : 'text-slate-400 hover:text-slate-300'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-slate-900/30 to-blue-900/20 p-6 rounded-lg border border-cyan-500/30">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-cyan-300">Active Employees</h3>
                <Users className="text-cyan-400" size={24} />
              </div>
              <p className="text-3xl font-bold text-white">{dashboard.active_employees}</p>
            </div>

            <div className="bg-slate-800/50 p-6 rounded-lg border border-cyan-500/20 border-l-4 border-l-green-500">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-green-400">YTD Gross Payroll</h3>
                <DollarSign className="text-green-400" size={24} />
              </div>
              <p className="text-2xl font-bold text-white">{formatCurrency(dashboard.ytd_gross_payroll)}</p>
            </div>

            <div className="bg-slate-800/50 p-6 rounded-lg border border-cyan-500/20 border-l-4 border-l-red-500">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-red-400">YTD Federal Taxes</h3>
                <Building className="text-red-400" size={24} />
              </div>
              <p className="text-2xl font-bold text-white">{formatCurrency(dashboard.ytd_federal_taxes)}</p>
            </div>

            <div className="bg-slate-800/50 p-6 rounded-lg border border-cyan-500/20 border-l-4 border-l-yellow-500">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-yellow-400">Quarterly Liability</h3>
                <Clock className="text-yellow-400" size={24} />
              </div>
              <p className="text-2xl font-bold text-white">{formatCurrency(dashboard.quarterly_tax_liability)}</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <button
              onClick={() => setShowRunForm(true)}
              className="p-6 bg-slate-800/50 border-2 border-cyan-500/20 rounded-lg hover:border-cyan-500/50 transition text-left group"
            >
              <div className="flex items-center mb-3">
                <div className="bg-cyan-500/20 p-3 rounded-full mr-4 group-hover:bg-cyan-500/30 transition">
                  <Play className="text-cyan-400" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-white">Run Payroll</h3>
                  <p className="text-sm text-slate-400">Start a new payroll run</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setShowEmployeeForm(true)}
              className="p-6 bg-slate-800/50 border-2 border-cyan-500/20 rounded-lg hover:border-green-500/50 transition text-left group"
            >
              <div className="flex items-center mb-3">
                <div className="bg-green-500/20 p-3 rounded-full mr-4 group-hover:bg-green-500/30 transition">
                  <Plus className="text-green-400" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-white">Add Employee</h3>
                  <p className="text-sm text-slate-400">Add a new payroll employee</p>
                </div>
              </div>
            </button>
          </div>

          {/* Recent Payroll Runs */}
          {dashboard.recent_payroll_runs && dashboard.recent_payroll_runs.length > 0 && (
            <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20">
              <div className="px-6 py-4 border-b border-cyan-500/20">
                <h3 className="text-lg font-semibold text-white">Recent Payroll Runs</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-slate-800">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Pay Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Period</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Employees</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Gross</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Net</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.recent_payroll_runs.map((run) => (
                      <tr key={run.id} className="border-t border-cyan-500/10 hover:bg-slate-700/50">
                        <td className="py-3 px-4 text-sm text-white">{formatDate(run.pay_date)}</td>
                        <td className="py-3 px-4 text-sm text-slate-300">
                          {formatDate(run.pay_period_start)} - {formatDate(run.pay_period_end)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm text-white">{run.employee_count}</td>
                        <td className="py-3 px-4 text-right text-sm text-white">{formatCurrency(run.total_gross)}</td>
                        <td className="py-3 px-4 text-right text-sm font-medium text-green-400">{formatCurrency(run.total_net)}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(run.status)}`}>
                            {run.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Employees Tab */}
      {activeTab === 'employees' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-white">Employees</h2>
            <button
              onClick={() => setShowEmployeeForm(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-2 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition"
            >
              <Plus size={20} />
              Add Employee
            </button>
          </div>

          <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20">
            {employees.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-slate-800">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Title</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Department</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Pay Type</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Pay Rate</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp) => (
                      <tr key={emp.id} className="border-t border-cyan-500/10 hover:bg-slate-700/50 cursor-pointer"
                          onClick={() => setSelectedEmployee(emp)}>
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium text-white">{emp.first_name} {emp.last_name}</div>
                          <div className="text-xs text-slate-400">{emp.email}</div>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-300">{emp.job_title || '-'}</td>
                        <td className="py-3 px-4 text-sm text-slate-300">{emp.department || '-'}</td>
                        <td className="py-3 px-4 text-sm text-slate-300">{emp.pay_type}</td>
                        <td className="py-3 px-4 text-right text-sm text-white">
                          {emp.pay_type === 'HOURLY' ? `${formatCurrency(emp.pay_rate)}/hr` : `${formatCurrency(emp.pay_rate)}/yr`}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 text-xs rounded-full ${emp.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                            {emp.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-12 text-center">
                <Users className="mx-auto text-slate-500 mb-4" size={48} />
                <h3 className="text-lg font-medium text-white mb-2">No Employees Yet</h3>
                <p className="text-cyan-300 mb-4">Add your first employee to get started with payroll.</p>
                <button
                  onClick={() => setShowEmployeeForm(true)}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-2 rounded-lg"
                >
                  <Plus size={20} />
                  Add Employee
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payroll Runs Tab */}
      {activeTab === 'payroll-runs' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-white">Payroll Runs</h2>
            <button
              onClick={() => setShowRunForm(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-2 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition"
            >
              <Plus size={20} />
              New Payroll Run
            </button>
          </div>

          <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20">
            {payrollRuns.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-slate-800">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Pay Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Period</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Employees</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Gross</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Taxes</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Net</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Status</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payrollRuns.map((run) => (
                      <tr key={run.id} className="border-t border-cyan-500/10 hover:bg-slate-700/50">
                        <td className="py-3 px-4 text-sm text-white">{formatDate(run.pay_date)}</td>
                        <td className="py-3 px-4 text-sm text-slate-300">
                          {formatDate(run.pay_period_start)} - {formatDate(run.pay_period_end)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm text-white">{run.employee_count}</td>
                        <td className="py-3 px-4 text-right text-sm text-white">{formatCurrency(run.total_gross)}</td>
                        <td className="py-3 px-4 text-right text-sm text-red-400">
                          {formatCurrency(run.total_federal_tax + run.total_social_security + run.total_medicare)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-medium text-green-400">{formatCurrency(run.total_net)}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(run.status)}`}>
                            {run.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {run.status === 'DRAFT' && (
                              <button
                                onClick={() => openEmployeeSelectModal(run.id)}
                                className="px-2 py-1 text-xs bg-cyan-500/20 text-cyan-400 rounded hover:bg-cyan-500/30"
                              >
                                Calculate
                              </button>
                            )}
                            {run.status === 'PENDING' && (
                              <button
                                onClick={() => handleApprovePayroll(run.id)}
                                className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30"
                              >
                                Approve
                              </button>
                            )}
                            {run.status === 'APPROVED' && (
                              <>
                                <button
                                  onClick={() => handleShowPaymentExport(run.id)}
                                  className="px-2 py-1 text-xs bg-cyan-500/20 text-cyan-400 rounded hover:bg-cyan-500/30"
                                >
                                  Process
                                </button>
                                <button
                                  onClick={() => handleMarkPaid(run.id)}
                                  className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded hover:bg-green-500/30"
                                >
                                  Mark Paid
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => viewPayrollRun(run.id)}
                              className="px-2 py-1 text-xs bg-slate-600/50 text-slate-300 rounded hover:bg-slate-600"
                            >
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-12 text-center">
                <Calendar className="mx-auto text-slate-500 mb-4" size={48} />
                <h3 className="text-lg font-medium text-white mb-2">No Payroll Runs Yet</h3>
                <p className="text-cyan-300 mb-4">Create your first payroll run to process employee payments.</p>
                <button
                  onClick={() => setShowRunForm(true)}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-2 rounded-lg"
                >
                  <Plus size={20} />
                  Create Payroll Run
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tax Calculator Tab */}
      {activeTab === 'tax-calculator' && (
        <TaxCalculator />
      )}

      {/* Employee Form Modal */}
      {showEmployeeForm && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-cyan-500/30">
            <div className="sticky top-0 bg-slate-900 border-b border-cyan-500/30 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Add Employee</h2>
              <button onClick={() => setShowEmployeeForm(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreateEmployee} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">First Name *</label>
                  <input
                    type="text"
                    required
                    value={employeeForm.first_name}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, first_name: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Last Name *</label>
                  <input
                    type="text"
                    required
                    value={employeeForm.last_name}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, last_name: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Email</label>
                  <input
                    type="email"
                    value={employeeForm.email}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Phone</label>
                  <input
                    type="text"
                    value={employeeForm.phone}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Hire Date *</label>
                  <input
                    type="date"
                    required
                    value={employeeForm.hire_date}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, hire_date: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">SSN</label>
                  <input
                    type="text"
                    placeholder="XXX-XX-XXXX"
                    value={employeeForm.ssn}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, ssn: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Job Title</label>
                  <input
                    type="text"
                    value={employeeForm.job_title}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, job_title: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Department</label>
                  <input
                    type="text"
                    value={employeeForm.department}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, department: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  />
                </div>
              </div>

              <hr className="border-cyan-500/20" />
              <h3 className="text-lg font-semibold text-white">Compensation</h3>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Pay Type *</label>
                  <select
                    value={employeeForm.pay_type}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, pay_type: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  >
                    <option value="SALARY">Salary</option>
                    <option value="HOURLY">Hourly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">
                    {employeeForm.pay_type === 'HOURLY' ? 'Hourly Rate *' : 'Annual Salary *'}
                  </label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    value={employeeForm.pay_rate}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, pay_rate: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Pay Frequency *</label>
                  <select
                    value={employeeForm.pay_frequency}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, pay_frequency: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  >
                    <option value="WEEKLY">Weekly</option>
                    <option value="BIWEEKLY">Bi-Weekly</option>
                    <option value="SEMIMONTHLY">Semi-Monthly</option>
                    <option value="MONTHLY">Monthly</option>
                  </select>
                </div>
              </div>

              <hr className="border-cyan-500/20" />
              <h3 className="text-lg font-semibold text-white">Tax Withholding (W-4)</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Filing Status</label>
                  <select
                    value={employeeForm.filing_status}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, filing_status: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  >
                    <option value="SINGLE">Single</option>
                    <option value="MARRIED_FILING_JOINTLY">Married Filing Jointly</option>
                    <option value="MARRIED_FILING_SEPARATELY">Married Filing Separately</option>
                    <option value="HEAD_OF_HOUSEHOLD">Head of Household</option>
                  </select>
                </div>
                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={employeeForm.w4_step2_checkbox}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, w4_step2_checkbox: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm text-cyan-300">Multiple Jobs (W-4 Step 2)</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Dependents Credit (Step 3)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={employeeForm.w4_dependents_amount}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, w4_dependents_amount: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Extra Withholding (Step 4c)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={employeeForm.w4_extra_withholding}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, w4_extra_withholding: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  />
                </div>
              </div>

              <hr className="border-cyan-500/20" />
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <CreditCard size={20} className="text-cyan-400" />
                Direct Deposit
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Bank Name</label>
                  <input
                    type="text"
                    value={employeeForm.bank_name}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, bank_name: e.target.value })}
                    placeholder="e.g., Chase, Bank of America"
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Account Type</label>
                  <select
                    value={employeeForm.bank_account_type}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, bank_account_type: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                  >
                    <option value="CHECKING">Checking</option>
                    <option value="SAVINGS">Savings</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Routing Number</label>
                  <input
                    type="text"
                    value={employeeForm.bank_routing}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, bank_routing: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                    placeholder="9 digits"
                    maxLength={9}
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-cyan-300 mb-2">Account Number</label>
                  <input
                    type="password"
                    value={employeeForm.bank_account}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, bank_account: e.target.value.replace(/\D/g, '') })}
                    placeholder="Account number"
                    className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white font-mono"
                  />
                </div>
              </div>

              <p className="text-xs text-slate-400">Bank info is encrypted and used for direct deposit payments.</p>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEmployeeForm(false)}
                  className="px-6 py-2 border border-cyan-500/30 text-slate-300 rounded-lg hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600"
                >
                  Add Employee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payroll Run Form Modal */}
      {showRunForm && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-lg shadow-xl max-w-md w-full border border-cyan-500/30">
            <div className="border-b border-cyan-500/30 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">New Payroll Run</h2>
              <button onClick={() => setShowRunForm(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreatePayrollRun} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-cyan-300 mb-2">Pay Period Start *</label>
                <input
                  type="date"
                  required
                  value={runForm.pay_period_start}
                  onChange={(e) => setRunForm({ ...runForm, pay_period_start: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-cyan-300 mb-2">Pay Period End *</label>
                <input
                  type="date"
                  required
                  value={runForm.pay_period_end}
                  onChange={(e) => setRunForm({ ...runForm, pay_period_end: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-cyan-300 mb-2">Pay Date *</label>
                <input
                  type="date"
                  required
                  value={runForm.pay_date}
                  onChange={(e) => setRunForm({ ...runForm, pay_date: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowRunForm(false)}
                  className="px-6 py-2 border border-cyan-500/30 text-slate-300 rounded-lg hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600"
                >
                  Create Run
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payroll Run Detail Modal */}
      {selectedRun && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-cyan-500/30">
            <div className="sticky top-0 bg-slate-900 border-b border-cyan-500/30 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white">Payroll Run Details</h2>
                <p className="text-sm text-cyan-300">
                  {formatDate(selectedRun.pay_period_start)} - {formatDate(selectedRun.pay_period_end)}
                </p>
              </div>
              <button onClick={() => setSelectedRun(null)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-sm text-slate-400">Gross Pay</p>
                  <p className="text-xl font-bold text-white">{formatCurrency(selectedRun.total_gross)}</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-sm text-slate-400">Federal Tax</p>
                  <p className="text-xl font-bold text-red-400">{formatCurrency(selectedRun.total_federal_tax)}</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-sm text-slate-400">FICA</p>
                  <p className="text-xl font-bold text-red-400">{formatCurrency(selectedRun.total_social_security + selectedRun.total_medicare)}</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-sm text-slate-400">Net Pay</p>
                  <p className="text-xl font-bold text-green-400">{formatCurrency(selectedRun.total_net)}</p>
                </div>
              </div>

              {/* Pay Stubs */}
              {selectedRun.pay_stubs && selectedRun.pay_stubs.length > 0 && (
                <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20">
                  <div className="px-4 py-3 border-b border-cyan-500/20">
                    <h3 className="font-semibold text-white">Employee Pay Stubs</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-slate-800">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs font-medium text-slate-400">Employee</th>
                          <th className="text-right py-3 px-4 text-xs font-medium text-slate-400">Gross</th>
                          <th className="text-right py-3 px-4 text-xs font-medium text-slate-400">Fed Tax</th>
                          <th className="text-right py-3 px-4 text-xs font-medium text-slate-400">SS</th>
                          <th className="text-right py-3 px-4 text-xs font-medium text-slate-400">Medicare</th>
                          <th className="text-right py-3 px-4 text-xs font-medium text-slate-400">Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRun.pay_stubs.map((stub) => (
                          <tr key={stub.id} className="border-t border-cyan-500/10">
                            <td className="py-3 px-4 text-sm text-white">{stub.employee_name}</td>
                            <td className="py-3 px-4 text-right text-sm text-white">{formatCurrency(stub.gross_pay)}</td>
                            <td className="py-3 px-4 text-right text-sm text-red-400">{formatCurrency(stub.federal_income_tax)}</td>
                            <td className="py-3 px-4 text-right text-sm text-red-400">{formatCurrency(stub.social_security_tax)}</td>
                            <td className="py-3 px-4 text-right text-sm text-red-400">{formatCurrency(stub.medicare_tax)}</td>
                            <td className="py-3 px-4 text-right text-sm font-medium text-green-400">{formatCurrency(stub.net_pay)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Employer Taxes */}
              <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20 p-4">
                <h3 className="font-semibold text-white mb-3">Employer Tax Liability</h3>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">Employer SS (6.2%)</p>
                    <p className="font-medium text-white">{formatCurrency(selectedRun.employer_social_security)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Employer Medicare (1.45%)</p>
                    <p className="font-medium text-white">{formatCurrency(selectedRun.employer_medicare)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">FUTA</p>
                    <p className="font-medium text-white">{formatCurrency(selectedRun.employer_futa)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">FL SUTA</p>
                    <p className="font-medium text-white">{formatCurrency(selectedRun.employer_suta)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employee Selection Modal */}
      {showEmployeeSelect && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-lg shadow-xl max-w-lg w-full border border-cyan-500/30">
            <div className="border-b border-cyan-500/30 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white">Select Employees</h2>
                <p className="text-sm text-cyan-300 mt-1">Choose which employees to include in this payroll run</p>
              </div>
              <button onClick={() => setShowEmployeeSelect(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <div className="p-6">
              {/* Quick Actions */}
              <div className="flex gap-3 mb-4">
                <button
                  onClick={selectAllEmployees}
                  className="text-sm text-cyan-400 hover:text-cyan-300"
                >
                  Select All
                </button>
                <span className="text-slate-600">|</span>
                <button
                  onClick={selectNoneEmployees}
                  className="text-sm text-cyan-400 hover:text-cyan-300"
                >
                  Select None
                </button>
                <span className="ml-auto text-sm text-slate-400">
                  {selectedEmployeeIds.length} of {employees.length} selected
                </span>
              </div>

              {/* Employee List */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {employees.map(emp => (
                  <label
                    key={emp.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition ${
                      selectedEmployeeIds.includes(emp.id)
                        ? 'bg-cyan-500/20 border border-cyan-500/30'
                        : 'bg-slate-800/50 border border-transparent hover:border-cyan-500/20'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEmployeeIds.includes(emp.id)}
                      onChange={() => toggleEmployeeSelection(emp.id)}
                      className="w-5 h-5 rounded border-cyan-500/50 text-cyan-500 focus:ring-cyan-500 bg-slate-800"
                    />
                    <div className="flex-1">
                      <div className="text-white font-medium">{emp.first_name} {emp.last_name}</div>
                      <div className="text-sm text-slate-400">
                        {emp.pay_type === 'HOURLY'
                          ? `${formatCurrency(emp.pay_rate)}/hr`
                          : `${formatCurrency(emp.pay_rate)}/yr`}
                        {emp.pay_frequency && ` · ${emp.pay_frequency.replace('_', '-')}`}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-cyan-500/20">
                <button
                  onClick={() => setShowEmployeeSelect(false)}
                  className="px-6 py-2 border border-cyan-500/30 text-slate-300 rounded-lg hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCalculatePayroll}
                  disabled={selectedEmployeeIds.length === 0}
                  className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Calculate for {selectedEmployeeIds.length} Employee{selectedEmployeeIds.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Export Modal */}
      {showPaymentExport && paymentExportData && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto border border-cyan-500/30">
            <div className="sticky top-0 bg-slate-900 border-b border-cyan-500/30 px-6 py-4 flex justify-between items-center z-10">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Banknote className="text-green-400" size={24} />
                  Process Payroll Payments
                </h2>
                <p className="text-sm text-cyan-300 mt-1">
                  Pay Date: {formatDate(paymentExportData.pay_date)} | Period: {formatDate(paymentExportData.pay_period_start)} - {formatDate(paymentExportData.pay_period_end)}
                </p>
              </div>
              <button onClick={() => setShowPaymentExport(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Payment Checklist */}
              <div className="bg-gradient-to-br from-slate-900/30 to-blue-900/20 rounded-lg border border-cyan-500/30 p-4">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <CheckCircle size={18} className="text-cyan-400" />
                  Payment Checklist
                </h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={paymentChecklist.employeePaymentsInitiated}
                      onChange={(e) => setPaymentChecklist({ ...paymentChecklist, employeePaymentsInitiated: e.target.checked })}
                      className="w-5 h-5 rounded border-cyan-500/50 text-cyan-500 focus:ring-cyan-500 bg-slate-800"
                    />
                    <span className={`text-sm ${paymentChecklist.employeePaymentsInitiated ? 'text-green-400 line-through' : 'text-white'}`}>
                      Employee direct deposits initiated via bank ({formatCurrency(paymentExportData.total_net_pay)})
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={paymentChecklist.eftpsSubmitted}
                      onChange={(e) => setPaymentChecklist({ ...paymentChecklist, eftpsSubmitted: e.target.checked })}
                      className="w-5 h-5 rounded border-cyan-500/50 text-cyan-500 focus:ring-cyan-500 bg-slate-800"
                    />
                    <span className={`text-sm ${paymentChecklist.eftpsSubmitted ? 'text-green-400 line-through' : 'text-white'}`}>
                      Federal taxes submitted via EFTPS ({formatCurrency(paymentExportData.tax_summary?.eftps_deposit || 0)})
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={paymentChecklist.flSutaSubmitted}
                      onChange={(e) => setPaymentChecklist({ ...paymentChecklist, flSutaSubmitted: e.target.checked })}
                      className="w-5 h-5 rounded border-cyan-500/50 text-cyan-500 focus:ring-cyan-500 bg-slate-800"
                    />
                    <span className={`text-sm ${paymentChecklist.flSutaSubmitted ? 'text-green-400 line-through' : 'text-white'}`}>
                      FL Reemployment Tax submitted ({formatCurrency(paymentExportData.tax_summary?.fl_suta || 0)})
                    </span>
                  </label>
                </div>
              </div>

              {/* Mercury Integration Section */}
              {mercuryStatus?.connected && (
                <div className="bg-gradient-to-br from-blue-900/30 to-cyan-900/20 rounded-lg border border-blue-500/30 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <Banknote size={18} className="text-blue-400" />
                      Mercury Bank Integration
                    </h3>
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Connected</span>
                  </div>

                  {mercuryResult ? (
                    <div className={`p-4 rounded-lg ${mercuryResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                      {mercuryResult.success ? (
                        <>
                          <p className="text-green-400 font-medium">Payments submitted to Mercury!</p>
                          <p className="text-sm text-green-300 mt-1">
                            {mercuryResult.successful?.length} payment(s) totaling {formatCurrency(mercuryResult.total_submitted)} queued for approval.
                          </p>
                          <p className="text-xs text-slate-400 mt-2">{mercuryResult.next_step}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-red-400 font-medium">Submission failed</p>
                          <p className="text-sm text-red-300 mt-1">{mercuryResult.error}</p>
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Sync Section */}
                      {mercuryRecipients.length === 0 ? (
                        <div className="mb-4 p-4 bg-slate-800/50 rounded-lg">
                          <p className="text-sm text-slate-300 mb-3">
                            No Mercury recipients found. Sync employees to create them automatically.
                          </p>
                          <button
                            onClick={handleSyncToMercury}
                            disabled={mercurySyncing}
                            className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 disabled:opacity-50 flex items-center gap-2"
                          >
                            {mercurySyncing ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-400 border-t-transparent"></div>
                                Syncing...
                              </>
                            ) : (
                              <>
                                <Users size={16} />
                                Sync Employees to Mercury
                              </>
                            )}
                          </button>
                          {mercurySyncResult && (
                            <p className={`text-sm mt-2 ${mercurySyncResult.success ? 'text-green-400' : 'text-red-400'}`}>
                              {mercurySyncResult.message}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-sm text-slate-300">
                            Map employees to Mercury recipients, then submit payments.
                          </p>
                          <button
                            onClick={handleSyncToMercury}
                            disabled={mercurySyncing}
                            className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                          >
                            {mercurySyncing ? 'Syncing...' : 'Sync New Employees'}
                          </button>
                        </div>
                      )}

                      {mercuryRecipients.length > 0 && (
                        <>
                          <div className="space-y-2 mb-4">
                            {paymentExportData.employee_payments?.map((emp) => (
                              <div key={emp.employee_id} className="flex items-center justify-between gap-4 p-2 bg-slate-800/50 rounded">
                                <div className="flex-1">
                                  <span className="text-white text-sm">{emp.employee_name}</span>
                                  <span className="text-green-400 text-sm ml-2">{formatCurrency(emp.net_pay)}</span>
                                </div>
                                <select
                                  value={employeeMercuryMappings[emp.employee_id] || ''}
                                  onChange={(e) => handleMercuryMappingChange(emp.employee_id, e.target.value)}
                                  className="px-3 py-1 bg-slate-700 border border-slate-600 rounded text-sm text-white min-w-[200px]"
                                >
                                  <option value="">Select Mercury recipient...</option>
                                  {mercuryRecipients.map(r => (
                                    <option key={r.id} value={r.id}>
                                      {r.name} {r.account_last_four ? `(...${r.account_last_four})` : ''}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            ))}
                          </div>

                          <button
                            onClick={handleSubmitToMercury}
                            disabled={mercurySubmitting || Object.values(employeeMercuryMappings).filter(Boolean).length === 0}
                            className="w-full py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {mercurySubmitting ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                Submitting...
                              </>
                            ) : (
                              <>
                                <Banknote size={18} />
                                Submit to Mercury ({Object.values(employeeMercuryMappings).filter(Boolean).length} payments)
                              </>
                            )}
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Mercury Not Configured Notice */}
              {mercuryStatus && !mercuryStatus.connected && (
                <div className="bg-slate-800/50 rounded-lg border border-slate-600 p-4">
                  <div className="flex items-center gap-3">
                    <Banknote size={24} className="text-slate-500" />
                    <div>
                      <p className="text-slate-300 font-medium">Mercury Bank Integration</p>
                      <p className="text-sm text-slate-500">
                        {mercuryStatus.configured
                          ? 'Connection failed. Check API credentials.'
                          : 'Not configured. Add MERCURY_API_KEY to enable automatic payments.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Employee Payments Section (Manual) */}
              <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20">
                <div className="px-4 py-3 border-b border-cyan-500/20 flex justify-between items-center">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <Users size={18} className="text-cyan-400" />
                    Employee Payments {mercuryStatus?.connected && <span className="text-xs text-slate-500">(Manual Reference)</span>}
                  </h3>
                  <button
                    onClick={copyEmployeeListToClipboard}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-cyan-500/20 text-cyan-400 rounded hover:bg-cyan-500/30 transition"
                  >
                    {copiedEmployeeList ? <CheckCircle size={16} /> : <Copy size={16} />}
                    {copiedEmployeeList ? 'Copied!' : 'Copy List'}
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-slate-800">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-400">Employee</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-400">Bank</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-400">Routing #</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-400">Account #</th>
                        <th className="text-center py-3 px-4 text-xs font-medium text-slate-400">Type</th>
                        <th className="text-right py-3 px-4 text-xs font-medium text-slate-400">Net Pay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentExportData.employee_payments?.map((emp, idx) => (
                        <tr key={idx} className="border-t border-cyan-500/10">
                          <td className="py-3 px-4 text-sm text-white font-medium">{emp.employee_name}</td>
                          <td className="py-3 px-4 text-sm text-slate-300">{emp.bank_name || <span className="text-red-400">Missing</span>}</td>
                          <td className="py-3 px-4 text-sm text-slate-300 font-mono">{emp.routing_number || <span className="text-red-400">Missing</span>}</td>
                          <td className="py-3 px-4 text-sm text-slate-300 font-mono">{emp.account_number || <span className="text-red-400">Missing</span>}</td>
                          <td className="py-3 px-4 text-sm text-slate-300 text-center">{emp.account_type || '-'}</td>
                          <td className="py-3 px-4 text-sm text-right font-medium text-green-400">{formatCurrency(emp.net_pay)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-800/50">
                      <tr className="border-t-2 border-cyan-500/30">
                        <td colSpan={5} className="py-3 px-4 text-sm font-semibold text-white text-right">Total Direct Deposit:</td>
                        <td className="py-3 px-4 text-right text-lg font-bold text-green-400">{formatCurrency(paymentExportData.total_net_pay)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Tax Deposits Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* EFTPS Federal Taxes */}
                <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <Building size={18} className="text-blue-400" />
                      Federal Taxes (EFTPS)
                    </h3>
                    <a
                      href="https://www.eftps.gov"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                    >
                      EFTPS.gov <ExternalLink size={14} />
                    </a>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Federal Income Tax (withheld)</span>
                      <span className="text-white">{formatCurrency(paymentExportData.tax_summary?.federal_income_tax)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Social Security (employee + employer)</span>
                      <span className="text-white">{formatCurrency(paymentExportData.tax_summary?.social_security_total)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Medicare (employee + employer)</span>
                      <span className="text-white">{formatCurrency(paymentExportData.tax_summary?.medicare_total)}</span>
                    </div>
                    <div className="flex justify-between border-t border-cyan-500/20 pt-2 mt-2">
                      <span className="font-semibold text-white">Total EFTPS Deposit</span>
                      <span className="font-bold text-blue-400">{formatCurrency(paymentExportData.tax_summary?.eftps_deposit)}</span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-blue-500/10 rounded-lg">
                    <p className="text-xs text-blue-300">
                      <strong>Schedule:</strong> Semi-weekly depositor - deposit by Wednesday for Sat-Tue wages,
                      by Friday for Wed-Fri wages.
                    </p>
                  </div>
                </div>

                {/* Florida SUTA */}
                <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <Building size={18} className="text-orange-400" />
                      Florida Reemployment Tax
                    </h3>
                    <a
                      href="https://floridarevenue.com/taxes/taxesfees/Pages/reemployment.aspx"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-orange-400 hover:text-orange-300"
                    >
                      FL Revenue <ExternalLink size={14} />
                    </a>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Taxable Wages</span>
                      <span className="text-white">{formatCurrency(paymentExportData.tax_summary?.total_gross)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">FL SUTA Rate</span>
                      <span className="text-white">2.7%</span>
                    </div>
                    <div className="flex justify-between border-t border-cyan-500/20 pt-2 mt-2">
                      <span className="font-semibold text-white">Total FL SUTA</span>
                      <span className="font-bold text-orange-400">{formatCurrency(paymentExportData.tax_summary?.fl_suta)}</span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-orange-500/10 rounded-lg">
                    <p className="text-xs text-orange-300">
                      <strong>Schedule:</strong> Quarterly filing - due by end of month following quarter end.
                      Q1: Apr 30, Q2: Jul 31, Q3: Oct 31, Q4: Jan 31
                    </p>
                  </div>
                </div>
              </div>

              {/* FUTA Note */}
              {paymentExportData.tax_summary?.futa > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Building size={20} className="text-yellow-400 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-yellow-400">FUTA Tax: {formatCurrency(paymentExportData.tax_summary?.futa)}</h4>
                      <p className="text-sm text-yellow-300 mt-1">
                        Federal Unemployment Tax is paid quarterly via EFTPS (Form 940).
                        Deposit when cumulative liability exceeds $500.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-4 border-t border-cyan-500/20">
                <p className="text-sm text-slate-400">
                  Complete checklist items above, then mark payroll as paid.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPaymentExport(false)}
                    className="px-6 py-2 border border-cyan-500/30 text-slate-300 rounded-lg hover:bg-slate-800"
                  >
                    Close
                  </button>
                  <button
                    onClick={async () => {
                      if (paymentExportData?.run_id) {
                        await handleMarkPaid(paymentExportData.run_id);
                        setShowPaymentExport(false);
                      }
                    }}
                    disabled={!paymentChecklist.employeePaymentsInitiated || !paymentChecklist.eftpsSubmitted}
                    className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <CheckCircle size={18} />
                    Mark Payroll as Paid
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Tax Calculator Component
function TaxCalculator() {
  const [form, setForm] = useState({
    gross_pay: '',
    pay_frequency: 'BIWEEKLY',
    filing_status: 'SINGLE',
    w4_step2_checkbox: false,
    w4_dependents_amount: 0,
    w4_extra_withholding: 0,
    ytd_gross: 0
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateTax = async () => {
    if (!form.gross_pay) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/payroll/tax-preview`, {
        ...form,
        gross_pay: parseFloat(form.gross_pay)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(response.data);
    } catch (err) {
      console.error('Error calculating tax:', err);
      alert('Failed to calculate taxes');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Input Form */}
      <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20 p-6">
        <h2 className="text-xl font-bold text-white mb-6">Tax Calculator</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-cyan-300 mb-2">Gross Pay *</label>
            <input
              type="number"
              step="0.01"
              value={form.gross_pay}
              onChange={(e) => setForm({ ...form, gross_pay: e.target.value })}
              placeholder="Enter gross pay for this period"
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-cyan-300 mb-2">Pay Frequency</label>
            <select
              value={form.pay_frequency}
              onChange={(e) => setForm({ ...form, pay_frequency: e.target.value })}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
            >
              <option value="WEEKLY">Weekly</option>
              <option value="BIWEEKLY">Bi-Weekly</option>
              <option value="SEMIMONTHLY">Semi-Monthly</option>
              <option value="MONTHLY">Monthly</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-cyan-300 mb-2">Filing Status</label>
            <select
              value={form.filing_status}
              onChange={(e) => setForm({ ...form, filing_status: e.target.value })}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
            >
              <option value="SINGLE">Single</option>
              <option value="MARRIED_FILING_JOINTLY">Married Filing Jointly</option>
              <option value="MARRIED_FILING_SEPARATELY">Married Filing Separately</option>
              <option value="HEAD_OF_HOUSEHOLD">Head of Household</option>
            </select>
          </div>

          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={form.w4_step2_checkbox}
                onChange={(e) => setForm({ ...form, w4_step2_checkbox: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm text-cyan-300">Multiple Jobs (W-4 Step 2)</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-cyan-300 mb-2">YTD Gross (for SS cap)</label>
            <input
              type="number"
              step="0.01"
              value={form.ytd_gross}
              onChange={(e) => setForm({ ...form, ytd_gross: parseFloat(e.target.value) || 0 })}
              className="w-full px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
            />
          </div>

          <button
            onClick={calculateTax}
            disabled={loading || !form.gross_pay}
            className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50"
          >
            {loading ? 'Calculating...' : 'Calculate Taxes'}
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="bg-slate-800/50 rounded-lg border border-cyan-500/20 p-6">
        <h2 className="text-xl font-bold text-white mb-6">Tax Breakdown</h2>

        {result ? (
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-cyan-500/20">
              <span className="text-slate-300">Gross Pay</span>
              <span className="text-xl font-bold text-white">{formatCurrency(result.gross_pay)}</span>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center py-1">
                <span className="text-slate-400">Federal Income Tax</span>
                <span className="text-red-400">-{formatCurrency(result.federal_income_tax)}</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-slate-400">Social Security (6.2%)</span>
                <span className="text-red-400">-{formatCurrency(result.social_security_tax)}</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-slate-400">Medicare (1.45%)</span>
                <span className="text-red-400">-{formatCurrency(result.medicare_tax)}</span>
              </div>
              {result.additional_medicare_tax > 0 && (
                <div className="flex justify-between items-center py-1">
                  <span className="text-slate-400">Additional Medicare (0.9%)</span>
                  <span className="text-red-400">-{formatCurrency(result.additional_medicare_tax)}</span>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center py-2 border-t border-cyan-500/20">
              <span className="text-slate-300">Total Taxes</span>
              <span className="text-lg font-bold text-red-400">-{formatCurrency(result.total_taxes)}</span>
            </div>

            <div className="flex justify-between items-center py-3 border-t-2 border-cyan-500/30 mt-4">
              <span className="text-lg text-white">Net Pay</span>
              <span className="text-2xl font-bold text-green-400">{formatCurrency(result.net_pay)}</span>
            </div>

            <div className="bg-cyan-500/10 rounded-lg p-4 mt-4">
              <p className="text-sm text-cyan-300">
                <strong>Note:</strong> This is an estimate based on 2025 IRS tax tables.
                Florida has no state income tax. Actual withholding may vary based on
                pre-tax deductions and other factors.
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Calculator className="mx-auto text-slate-500 mb-4" size={48} />
            <p className="text-cyan-300">Enter gross pay and click calculate to see the tax breakdown</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Payroll;
